import React from "react";
import { connect } from "react-redux";

import Grid from "../core/components/grid/Grid";
import GridColumn from "../core/components/grid/GridColumn";
import { AppDispatch, IAppState } from "../core/state/Store";
import { Side } from "../core/types/system/Layout";
import Text from "../core/components/text/Text";
import { setPageLoading } from "../core/state/system/UIConfiguration";
import { unwrappedDispatch } from "../core/foundation/Dispatching";
import {
    downloadLogArchives,
    initialiseLogArchiveDetails,
} from "../core/state/system/LogArchives";
import LogArchive from "../core/types/system/LogArchive";
import MainToolbarContents from "../core/components/toolbar/MainToolbarContents";
import ToolbarButton from "../core/components/toolbar/ToolbarButton";

import "./LogArchives.scss";

interface RouteLogArchivesProps {
    archives: LogArchive[];

    setPageLoading: (loading: boolean) => void;
    initialiseLogArchiveDetails: () => Promise<void>;
    downloadLogArchives: (archives: LogArchive[]) => Promise<string[]>;
}

interface RouteLogArchivesState {
    selection?: LogArchive[];
}

class RouteLogArchives extends React.Component<
    RouteLogArchivesProps,
    RouteLogArchivesState
> {
    state: RouteLogArchivesState = {};

    async componentDidMount() {
        this.props.setPageLoading(true);
        await this.props.initialiseLogArchiveDetails();
        this.props.setPageLoading(false);
    }

    async download() {
        try {
            this.props.setPageLoading(true);

            (
                await Promise.all(
                    this.state.selection.map(async record => ({
                        name: record.name,
                        data: (
                            await this.props.downloadLogArchives([record])
                        )[0],
                    })),
                )
            ).download();
        } finally {
            this.props.setPageLoading(false);
        }
    }

    render() {
        return (
            <section className="full-height">
                <MainToolbarContents>
                    <ToolbarButton
                        icon="download"
                        text="Download"
                        tooltip="Download the selected log archive"
                        disabled={
                            !this.state.selection ||
                            this.state.selection.length === 0
                        }
                        onClick={this.download.bind(this)}
                    />
                </MainToolbarContents>

                {this.props.archives?.length > 0 ? (
                    <Grid<LogArchive>
                        value={this.props.archives}
                        window={true}
                        fixedHeader={true}
                        fullWidth={true}
                        keyField="name"
                        blockSort={true}
                        paged={true}
                        defaultSortField="-created"
                        selectionChanged={(record, selection) =>
                            this.setState({ selection })
                        }>
                        <GridColumn
                            header="Name"
                            field="name"
                            filterable={true}
                            selectable={true}
                        />
                        <GridColumn
                            header="Created Date"
                            field="created"
                            filterable={true}
                            selectable={true}
                        />
                    </Grid>
                ) : (
                    <Text clearanceSide={Side.All}>No archives found.</Text>
                )}
            </section>
        );
    }
}

const mapStateToProps = (state: IAppState) => {
    const { logArchives } = state.db;
    return {
        archives: logArchives.data,
    };
};

const mapDispatchToProps = (dispatch: AppDispatch) => {
    return {
        setPageLoading: (loading: boolean) => dispatch(setPageLoading(loading)),
        initialiseLogArchiveDetails: () =>
            unwrappedDispatch(initialiseLogArchiveDetails()),
        downloadLogArchives: (archives: LogArchive[]) =>
            unwrappedDispatch(downloadLogArchives(archives)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(RouteLogArchives);
